// Edit Mode File Handler
// Handles file restoration and management when editing existing courses

import { fileStorageService, FileStorageService } from './fileStorageService';
import { toast } from 'sonner';

export interface EditModeFileData {
  fileId?: string;
  filename: string;
  fileType: string;
  fileSize: number;
  fileCategory: string;
  url?: string;
  thumbnailUrl?: string;
  isExistingFile?: boolean;
  needsReupload?: boolean;
}

export interface MaterialFileData {
  content: EditModeFileData;
}

export interface TopicFileData {
  materials: MaterialFileData[];
}

export interface CourseFileData {
  topics: TopicFileData[];
  coverImage?: EditModeFileData;
}

/**
 * Restore files for edit mode from backend data
 */
export const restoreEditModeFiles = async (
  courseData: any,
  onFileRestored?: (fileId: string, file: File) => void
): Promise<CourseFileData> => {
  const restoredData: CourseFileData = {
    topics: [],
  };

  try {
    // Restore cover image if exists
    if (courseData.coverImage && courseData.coverImage.fileId) {
      try {
        const storedFile = await fileStorageService.getFile(courseData.coverImage.fileId);
        if (storedFile) {
          restoredData.coverImage = {
            fileId: courseData.coverImage.fileId,
            filename: storedFile.metadata.filename,
            fileType: storedFile.metadata.fileType,
            fileSize: storedFile.metadata.fileSize,
            fileCategory: storedFile.metadata.fileCategory,
            url: URL.createObjectURL(storedFile.file),
            isExistingFile: true,
          };
          onFileRestored?.(courseData.coverImage.fileId, storedFile.file);
        }
      } catch (error) {
        console.error('Error restoring cover image:', error);
        restoredData.coverImage = {
          ...courseData.coverImage,
          needsReupload: true,
        };
        toast.error('Cover image needs to be re-uploaded');
      }
    }

    // Restore topic materials
    if (courseData.topics) {
      for (const topic of courseData.topics) {
        const restoredTopic: TopicFileData = {
          materials: [],
        };

        if (topic.materials) {
          for (const material of topic.materials) {
            const restoredMaterial: MaterialFileData = {
              content: {
                filename: material.content?.filename || 'Unknown file',
                fileType: material.content?.fileType || 'UNKNOWN',
                fileSize: material.content?.fileSize || 0,
                fileCategory: material.content?.fileCategory || 'LEARNING_MATERIAL',
                isExistingFile: true,
              },
            };

            // Try to restore file from storage
            if (material.content?.fileId) {
              try {
                const storedFile = await fileStorageService.getFile(material.content.fileId);
                if (storedFile) {
                  restoredMaterial.content.fileId = material.content.fileId;
                  restoredMaterial.content.url = URL.createObjectURL(storedFile.file);
                  onFileRestored?.(material.content.fileId, storedFile.file);
                } else {
                  restoredMaterial.content.needsReupload = true;
                  toast.error(`File "${material.content.filename}" needs to be re-uploaded`);
                }
              } catch (error) {
                console.error(`Error restoring file for material ${material.id}:`, error);
                restoredMaterial.content.needsReupload = true;
                toast.error(`File "${material.content.filename}" needs to be re-uploaded`);
              }
            } else {
              // No fileId means file needs to be uploaded
              restoredMaterial.content.needsReupload = true;
            }

            restoredTopic.materials.push(restoredMaterial);
          }
        }

        restoredData.topics.push(restoredTopic);
      }
    }

    return restoredData;
  } catch (error) {
    console.error('Error restoring edit mode files:', error);
    toast.error('Failed to restore some files. Please re-upload them.');
    return restoredData;
  }
};

/**
 * Handle file replacement in edit mode
 */
export const replaceFileInEditMode = async (
  file: File,
  materialId: string,
  topicId: string,
  oldFileId?: string
): Promise<{ fileId: string; url: string }> => {
  try {
    // Validate file type
    if (!FileStorageService.validateFileType(file)) {
      throw new Error('Invalid file type. Please upload PDF, PPTX, or VIDEO files only.');
    }

    // Clean up old file if exists
    if (oldFileId) {
      try {
        await fileStorageService.deleteFile(oldFileId);
        console.log('Old file cleaned up:', oldFileId);
      } catch (error) {
        console.warn('Failed to clean up old file:', error);
      }
    }

    // Store new file
    const fileId = await fileStorageService.storeFile(file, {
      filename: file.name,
      fileType: FileStorageService.getFileTypeFromMime(file.type),
      fileSize: file.size,
      fileCategory: 'LEARNING_MATERIAL',
      topicId,
      materialId,
    });

    const url = URL.createObjectURL(file);

    console.log('File replaced successfully:', { oldFileId, newFileId: fileId });
    toast.success('File replaced successfully');
    return { fileId, url };
  } catch (error) {
    console.error('Error replacing file:', error);
    toast.error(error instanceof Error ? error.message : 'Failed to replace file');
    throw error;
  }
};

/**
 * Check if file needs re-upload in edit mode
 */
export const checkFileStatus = (fileData: EditModeFileData): {
  needsReupload: boolean;
  isExisting: boolean;
  hasFile: boolean;
} => {
  return {
    needsReupload: fileData.needsReupload || false,
    isExisting: fileData.isExistingFile || false,
    hasFile: !!(fileData.fileId || fileData.url),
  };
};

/**
 * Clean up file URLs to prevent memory leaks
 */
export const cleanupFileUrls = (fileData: EditModeFileData): void => {
  if (fileData.url && fileData.url.startsWith('blob:')) {
    URL.revokeObjectURL(fileData.url);
  }
  if (fileData.thumbnailUrl && fileData.thumbnailUrl.startsWith('blob:')) {
    URL.revokeObjectURL(fileData.thumbnailUrl);
  }
};

/**
 * Check if material has changes compared to original
 */
export const hasMaterialChanged = (currentMaterial: any, originalMaterial: any): boolean => {
  if (!originalMaterial) {
    console.log('Material is new (no original):', currentMaterial.title);
    return true; // New material
  }
  
  // Check file changes
  const currentFile = currentMaterial.content?.file;
  const originalFile = originalMaterial.content?.file;
  const fileChanged = !!currentFile !== !!originalFile || 
    (currentFile && originalFile && currentFile.name !== originalFile.name);
  
  // Check metadata changes
  const metadataChanged = 
    currentMaterial.title !== originalMaterial.title ||
    currentMaterial.description !== originalMaterial.description ||
    currentMaterial.duration !== originalMaterial.duration;
  
  // Check if file was replaced (has new fileId)
  const fileReplaced = currentMaterial.content?.fileId !== originalMaterial.content?.fileId;
  
  console.log('Material change detection:', {
    materialId: currentMaterial.id,
    materialTitle: currentMaterial.title,
    isNew: !originalMaterial,
    fileChanged,
    metadataChanged,
    fileReplaced,
    hasChanges: fileChanged || metadataChanged || fileReplaced
  });
  
  return fileChanged || metadataChanged || fileReplaced;
};

/**
 * Check if topic has changes compared to original
 */
export const hasTopicChanged = (currentTopic: any, originalTopic: any): boolean => {
  if (!originalTopic) return true; // New topic
  
  // Check topic metadata changes
  const topicMetadataChanged = 
    currentTopic.title !== originalTopic.title ||
    currentTopic.description !== originalTopic.description ||
    currentTopic.estimatedDuration !== originalTopic.estimatedDuration;
  
  // Check if materials changed
  const currentMaterials = currentTopic.materials || [];
  const originalMaterials = originalTopic.materials || [];
  
  if (currentMaterials.length !== originalMaterials.length) {
    return true; // Materials added/removed
  }
  
  // Check each material for changes
  for (let i = 0; i < currentMaterials.length; i++) {
    if (hasMaterialChanged(currentMaterials[i], originalMaterials[i])) {
      return true;
    }
  }
  
  console.log('Topic change detection:', {
    topicId: currentTopic.id,
    topicMetadataChanged,
    hasChanges: topicMetadataChanged
  });
  
  return topicMetadataChanged;
}; 